import { useEffect, useState } from "react";
import { Loader2, Wrench } from "lucide-react";
import { api } from "@/lib/api";

const day = (s) => (s ? new Date(s).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—");
const cad = (n, cents = true) => (n || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: cents ? 2 : 0 });
const state = (s) => (s || "").replace(/_/g, " ");
const SPLIT = [["labour", "Labour"], ["parts", "Parts"], ["equipment", "Equipment"], ["other", "Other"]];

function Totals({ t, big = false }) {
  return (
    <div className={`grid grid-cols-5 gap-1 text-center ${big ? "text-xs" : "text-[10px]"}`}>
      {SPLIT.map(([k, l]) => <div key={k} className="rounded-lg bg-black/5 py-1 min-w-0"><p className="font-semibold truncate">{cad(t[k], !big)}</p><p className="opacity-60 text-[9px] uppercase tracking-wider">{l}</p></div>)}
      <div className="rounded-lg bg-black/10 py-1 min-w-0"><p className="font-bold truncate">{cad(t.total, !big)}</p><p className="opacity-60 text-[9px] uppercase tracking-wider">Total</p></div>
    </div>
  );
}

function Record({ r }) {
  return (
    <details className="rounded-2xl bg-black/5 px-3 py-2" data-testid={`part-record-${r.kind}-${r.number}`}>
      <summary className="cursor-pointer text-sm">
        <span className="font-semibold">{r.kind === "ft" ? `FT ${r.number}` : r.kind === "inspection" ? "Inspection" : `WR ${r.number}`}</span>
        <span className="opacity-60"> · {day(r.performed)} · {state(r.state)}</span>
        {r.totals.total > 0 ? <span className="float-right font-semibold">{cad(r.totals.total)}</span> : r.quoted > 0 && <span className="float-right font-semibold text-amber-700" title="Estimate, not yet spent">{cad(r.quoted)} est.</span>}
        <span className="block text-xs opacity-80 mt-0.5">{r.description}</span>
      </summary>
      <div className="mt-2 space-y-2 text-xs">
        <p className="opacity-70">{[r.kind === "ft" && `WR ${r.wr}`, r.technician, r.company, r.signed_by && `signed off by ${r.signed_by} ${day(r.signed_on)}`].filter(Boolean).join(" · ")}</p>
        {r.work.map((w, i) => (
          <div key={i} className="space-y-1">
            <p className="font-semibold">{w.type}</p>
            {w.lines.map((l, j) => <div key={j} className="flex gap-2 border-t border-black/10 pt-1"><span className="capitalize w-16 shrink-0 opacity-70">{l.type}</span><span className="flex-1 min-w-0">{l.description}<span className="block opacity-60">{l.qty}</span></span><span className="shrink-0">{cad(l.total)}</span></div>)}
          </div>
        ))}
        {r.work.length > 0 && <Totals t={r.totals} />}
        {(r.items || []).map((x, i) => <div key={i} className="flex gap-2 border-t border-black/10 pt-1"><span className="capitalize flex-1 min-w-0">{x.item}{x.note && <span className="block opacity-70 normal-case">{x.note}</span>}</span>{x.status && <span className={`shrink-0 font-semibold uppercase text-[10px] ${x.status === "needs" ? "text-red-600" : "text-emerald-700"}`}>{x.status}</span>}</div>)}
        {(r.estimates || []).map((e, i) => <div key={`e${i}`} className="flex gap-2 border-t border-black/10 pt-1"><span className="w-16 shrink-0 opacity-70">Estimate</span><span className="flex-1 min-w-0">{[state(e.state), e.company, e.by, day(e.created)].filter(Boolean).join(" · ")}</span><span className="shrink-0 font-semibold text-amber-700">{cad(e.total)}</span></div>)}
        {r.kind === "wr" && <p className="opacity-60">{r.quoted > 0 ? "Quoted, not yet done. No field ticket yet, so nothing has been spent." : "No field ticket yet, so there are no costs."}</p>}
      </div>
    </details>
  );
}

export function PartRecords({ h }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api.get(`/public/hotspots/${h.id}/part-records`, { headers: { "X-Tour-Key": sessionStorage.getItem(`tourkey_${h.house_type_id}`) || "" } })
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.status === 502 ? "Pumpjack records are unavailable right now." : e.response?.status === 404 ? e.response.data?.detail || "" : "hide"));
  }, [h.id, h.house_type_id]);
  if (err === "hide") return null;
  if (err) return <p className="mt-3 text-xs opacity-70" data-testid="part-records-error">{err}</p>;
  if (!d) return <p className="mt-3 text-xs opacity-70 inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" />Loading this part's records…</p>;
  return (
    <div className="mt-4 space-y-2" data-testid="part-records">
      <p className="text-xs opacity-70 flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" />{d.records.length} records for {d.part} · last work {day(d.last_done)}</p>
      <p className="text-[10px] uppercase tracking-wider opacity-60">Spent on this part (field tickets)</p>
      <Totals t={d.lifetime} big />
      {d.quoted > 0 && <p className="text-xs rounded-xl bg-amber-500/15 text-amber-800 px-3 py-2" data-testid="part-quoted">Plus <strong>{cad(d.quoted)}</strong> quoted in estimates that haven't been done yet.</p>}
      {d.records.map((r) => <Record key={`${r.kind}${r.number}`} r={r} />)}
      {!d.records.length && <p className="text-xs opacity-70">No records mention {d.keywords.join(", ")}{d.work_types.length ? ` or ${d.work_types.join(", ")}` : ""} on this well yet.</p>}
      <p className="text-[10px] opacity-50">Read-only from Pumpjack Connect · {d.well.surface}{d.well.downhole ? ` → ${d.well.downhole}` : ""}</p>
    </div>
  );
}
